import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  MenuItem,
  Typography,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Box,
  IconButton,
  Stack,
  InputAdornment,
  Tooltip,
  FormHelperText,
  FormControl,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProductStock } from "types/productStock.type";
import {
  ArrowBack,
  Delete,
  InfoOutlined,
  LocalOffer,
} from "@mui/icons-material";
import { Product } from "types/product.type";
import * as styles from "styles/components/product/ProductFormDialog.styles";
import { toast } from "react-toastify";
import { useStore } from "stores/useStore";
import { useNavigate, useParams } from "react-router-dom";
import FullScreenLoader from "components/loading/FullScreenLoader";
import { observer } from "mobx-react-lite";
import PriceInput from "components/Input/PriceInput";
import NumberInput from "components/Input/NumberInput";

const ProductFormQuantityPage = observer(() => {
  const {
    authStore,
    productStore,
    categoryStore,
    subcategoryStore,
    depotStore,
    companyStore,
  } = useStore();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();

  const initialForm: Partial<Product> = useMemo(
    () => ({
      name: "",
      reference: "",
      purchasePrice: undefined,
      salePrice: undefined,
      promoPrice: undefined,
      isOnPromo: false,
      weight: null,
      length: null,
      width: null,
      height: null,
      warrantyPeriod: null,
      isActive: true,
      codeQr: null,
      images: [],
      description: null,
      categoryId: null,
      subcategoryId: null,
      companyId: null,
      subCompanyId: null,
      stocks: [],
    }),
    []
  );

  const [form, setForm] = useState<Partial<Product>>(initialForm);
  const [selectedDepotIds, setSelectedDepotIds] = useState<number[]>([]);
  // const [addedQuantity, setAddedQuantity] = useState<string>("0");
  const [addedQuantities, setAddedQuantities] = useState<{
    [depotId: number]: number;
  }>({});

  const { categories, subcategories, depots } = useMemo(
    () => ({
      categories: categoryStore.categories || [],
      subcategories: subcategoryStore.subcategories || [],
      depots: depotStore.depots || [],
    }),
    [
      categoryStore.categories,
      subcategoryStore.subcategories,
      depotStore.depots,
    ]
  );
  const loading = productStore.loadingForm || false;

  const filteredSubcategories = useMemo(() => {
    return subcategories.filter((sub) => sub.categoryId === form.categoryId);
  }, [subcategories, form.categoryId]);

  const handleDepotSelectChange = (
    event: SelectChangeEvent<typeof selectedDepotIds>
  ) => {
    const value = event.target.value;
    if (!value) return;

    const newSelectedDepotIds =
      typeof value === "string"
        ? value.split(",").map(Number).filter(Number.isInteger)
        : value.filter(Number.isInteger);

    setSelectedDepotIds(newSelectedDepotIds);

    setForm((prev) => {
      const currentStocks = prev.stocks || [];
      // console.log("currentStocks =>", currentStocks);

      const existingStocksMap = new Map<number, ProductStock>();
      currentStocks.forEach((stock) => {
        if (stock.depotId !== undefined && stock.depotId) {
          existingStocksMap.set(stock.depotId, stock);
        }
      });
      const newStocks = newSelectedDepotIds.map((depotId) => {
        const existingStock = existingStocksMap.get(depotId);
        return (
          existingStock || {
            depotId,
            totalQuantity: 0,
            availableQuantity: 0,
            defective: 0,
            returned: 0,
            minQuantity: 10,
            saleQuantity: 0,
            isActive: true,
            variants: [],
          }
        );
      });
      const unselectedStocks = currentStocks.filter(
        (stock) =>
          stock.depotId !== undefined &&
          stock.depotId !== null &&
          !newSelectedDepotIds.includes(stock.depotId)
      );
      // console.log("newStocks =>", newStocks);
      return {
        ...prev,
        stocks: [...newStocks, ...unselectedStocks],
      };
    });
  };

  const getStockForDepot = useCallback(
    (depotId: number): ProductStock => {
      const stock = form.stocks?.find((s) => s.depotId === depotId);
      if (!stock) {
        return {
          depotId,
          totalQuantity: 0,
          defective: 0,
          availableQuantity: 0,
          saleQuantity: 0,
          returned: 0,
          minQuantity: 10,
          variants: [],
          isActive: true,
        };
      }

      const total = stock.totalQuantity ?? 0;
      const defective = stock.defective ?? 0;
      const sold = stock.saleQuantity ?? 0;

      return {
        depotId: stock.depotId,
        totalQuantity: total,
        defective: defective,
        availableQuantity:
          stock?.availableQuantity ?? Math.max(total - defective - sold, 0),
        returned: stock.returned ?? 0,
        minQuantity: stock.minQuantity ?? 10,
        saleQuantity: stock.saleQuantity ?? 0,
        variants: stock.variants,
        isActive: stock.isActive ?? true,
        id: stock.id,
        productId: stock.productId,
      };
    },
    [form.stocks]
  );

  const handleStockChange = (
    depotId: number,
    key: keyof ProductStock,
    value: string | boolean
  ) => {
    setForm((prev) => {
      const updatedStocks = [...(prev.stocks || [])];
      const existingIndex = updatedStocks.findIndex(
        (s) => s.depotId === depotId
      );

      let processedValue: number | string | boolean = value;
      if (typeof value === "string") {
        processedValue =
          value === "" ? 0 : Number(value.replace(/[^0-9]/g, ""));
        if (isNaN(processedValue as number)) processedValue = 0;
      }

      if (existingIndex >= 0) {
        const updatedStock = {
          ...updatedStocks[existingIndex],
          [key]: processedValue,
        };

        if (
          key === "totalQuantity" ||
          key === "defective" ||
          key === "saleQuantity"
        ) {
          const total = updatedStock.totalQuantity || 0;
          const defective = updatedStock.defective || 0;
          const saleQuantity = updatedStock.saleQuantity || 0;

          updatedStock.availableQuantity = Math.max(
            total - defective - saleQuantity,
            0
          );
        }

        updatedStocks[existingIndex] = updatedStock;
      } else {
        const newStock: ProductStock = {
          depotId,
          totalQuantity: 0,
          availableQuantity: 0,
          defective: 0,
          returned: 0,
          saleQuantity: 0,
          minQuantity: 10,
          variants: [],
          isActive: true,
          [key]: processedValue,
        };

        const total = newStock.totalQuantity || 0;
        const defective = newStock.defective || 0;
        const saleQuantity = newStock.saleQuantity || 0;
        newStock.availableQuantity = Math.max(
          total - defective - saleQuantity,
          0
        );

        updatedStocks.push(newStock);
      }

      return { ...prev, stocks: updatedStocks };
    });
  };

  const handleVariantChange = (
    depotId: number,
    index: number,
    key: string,
    value: string | number
  ) => {
    setForm((prev) => {
      const updatedStocks = [...(prev.stocks || [])];
      const stockIndex = updatedStocks.findIndex((s) => s.depotId === depotId);
      if (stockIndex < 0) return prev;

      const stock = updatedStocks[stockIndex];
      const variants = [...(stock.variants || [])];

      if (key === "totalQuantity") {
        let newQty = Number(value);
        if (isNaN(newQty) || newQty < 0) newQty = 0;

        variants[index] = { ...variants[index], totalQuantity: newQty };
      } else {
        variants[index] = { ...variants[index], [key]: value };
      }

      updatedStocks[stockIndex] = {
        ...stock,
        variants,
      };

      return { ...prev, stocks: updatedStocks };
    });
  };

  const addVariant = (depotId: number) => {
    setForm((prev) => {
      const updatedStocks = [...(prev.stocks || [])];
      const stockIndex = updatedStocks.findIndex((s) => s.depotId === depotId);
      if (stockIndex < 0) return prev;

      const variants = [...(updatedStocks[stockIndex].variants || [])];
      variants.push({
        color: "",
        size: "",
        gender: null,
        totalQuantity: 0,
        availableQuantity: 0,
        defectiveQuantity: 0,
        returnedQuantity: 0,
        saleQuantity: 0,
      });
      updatedStocks[stockIndex].variants = variants;

      return { ...prev, stocks: updatedStocks };
    });
  };

  const handleAddQuantity = (depotId: number, value: string) => {
    const newQty = Number(value) || 0;

    setAddedQuantities((prev) => {
      const prevQty = prev[depotId] || 0;
      const diff = newQty - prevQty;

      setForm((prevForm) => {
        const updatedStocks = [...(prevForm.stocks || [])];
        const stockIndex = updatedStocks.findIndex(
          (s) => s.depotId === depotId
        );
        if (stockIndex < 0) return prevForm;

        const stock = { ...updatedStocks[stockIndex] };
        const oldTotal = Number(stock.totalQuantity) || 0;
        const oldAvailable = Number(stock.availableQuantity) || 0;

        const newTotal = oldTotal + diff;
        stock.totalQuantity = newTotal;

        stock.availableQuantity = Math.max(oldAvailable + diff, 0);

        updatedStocks[stockIndex] = stock;
        return { ...prevForm, stocks: updatedStocks };
      });

      return { ...prev, [depotId]: newQty };
    });
  };

  const applyFormData = (data: Partial<Product>) => {
    // console.log("data =>", data);
    const processedData: Partial<Product> = {
      ...data,
      purchasePrice: data.purchasePrice ?? 0,
      salePrice: data.salePrice ?? 0,
      promoPrice: data.promoPrice ?? null,
      weight: data.weight ?? null,
      length: data.length ?? null,
      width: data.width ?? null,
      height: data.height ?? null,
      warrantyPeriod: data.warrantyPeriod ?? null,
      images: data.images ?? [],
    };

    setForm(processedData);

    const depotIds = (processedData.stocks || [])
      .map((s) => s.depotId)
      .filter((id): id is number => id != null);

    if (
      !data?.id &&
      depotIds.length === 0 &&
      depots.filter((d) => d.isActive).length === 1
    ) {
      const singleDepotId = depots.find((d) => d.isActive)?.id;
      if (singleDepotId) {
        setSelectedDepotIds([singleDepotId]);
        setForm((prev) => ({
          ...prev,
          stocks: [
            {
              depotId: singleDepotId,
              totalQuantity: 0,
              availableQuantity: 0,
              defective: 0,
              returned: 0,
              minQuantity: 10,
              isActive: true,
            },
          ],
        }));
        return;
      }
    }
    setSelectedDepotIds(depotIds);
  };

  const handleSubmit = useCallback(async () => {
    const success = form.id
      ? await productStore.updateProduct(form.id, form)
      : await productStore.createProduct(form);

    if (success) navigate("/products");
  }, [form, productStore, navigate]);

  const handleDeleteStock = async (depotId: number, stockId?: number) => {
    setForm((prev) => {
      const updatedStocks = (prev.stocks || []).filter(
        (s) => s.depotId !== depotId
      );
      return { ...prev, stocks: updatedStocks };
    });

    setSelectedDepotIds((prev) => prev.filter((id) => id !== depotId));
  };

  const isFormValid: boolean = useMemo(() => {
    // Convert to numbers once to ensure clean comparisons
    const pPrice = Number(form.purchasePrice);
    const sPrice = Number(form.salePrice);
    const prPrice = Number(form.promoPrice);

    const hasRequiredFields =
      !!form.name?.trim() &&
      !!form.reference?.trim() &&
      !!form.categoryId &&
      pPrice > 0 &&
      sPrice > 0 &&
      sPrice > pPrice; // Comparison between numbers

    const isPromoValid =
      !form.isOnPromo || (prPrice > 0 && sPrice > 0 && prPrice < sPrice);

    return !!(hasRequiredFields && isPromoValid);
  }, [
    form.name,
    form.reference,
    form.categoryId,
    form.purchasePrice,
    form.salePrice,
    form.isOnPromo,
    form.promoPrice,
  ]);

  const isVariantsQuantityValid = (stock: any) => {
    if (!stock.variants || stock.variants.length === 0) {
      return true;
    }
    // Compare against the remaining stock, not the initial total: units sold or
    // marked defective before variants existed are not attributed to any variant.
    const variantsAvailable = (stock.variants || []).reduce(
      (sum: number, v: any) =>
        sum +
        Math.max(
          (Number(v.totalQuantity) || 0) -
            (Number(v.defectiveQuantity) || 0) -
            (Number(v.saleQuantity) || 0),
          0
        ),
      0
    );
    return variantsAvailable === getStockAvailableQuantity(stock);
  };

  const getStockAvailableQuantity = (stock: any) =>
    Math.max(
      (Number(stock.totalQuantity) || 0) -
        (Number(stock.defective) || 0) -
        (Number(stock.saleQuantity) || 0),
      0
    );

  useEffect(() => {
    if (
      form.subcategoryId &&
      !filteredSubcategories.some((sub) => sub.id === form.subcategoryId)
    ) {
      setForm((prev) => ({ ...prev, subcategoryId: null }));
    }
  }, [form.categoryId, form.subcategoryId, filteredSubcategories]);

  useEffect(() => {
    categoryStore.fetchCategories();
    subcategoryStore.fetchSubcategories();
    depotStore.fetchDepots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authStore?.user?.companyId) {
      companyStore.fetchSubCompanies(authStore.user.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.user?.companyId]);

  useEffect(() => {
    const loadProduct = async () => {
      if (productId && productId !== "new") {
        let product = productStore.products.find(
          (p) => p.id === Number(productId)
        );

        if (!product) {
          try {
            const fetched = await productStore.fetchProductById(
              Number(productId)
            );
            if (!fetched) {
              toast.error("Produit introuvable.");
              navigate("/products");
            } else {
              applyFormData(fetched);
            }
          } catch (err) {
            toast.error("Erreur lors du chargement du produit.");
            navigate("/products");
          }
        } else {
          applyFormData(product);
        }
      }
    };

    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, productStore]);

  useEffect(() => {
    console.log("isFormValid ====>", isFormValid);
  }, []);

  return (
    <Box sx={styles.mainBox}>
      {loading && <FullScreenLoader />}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        px={1}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          disabled={loading}
        >
          Retour
        </Button>
        <Typography variant="h5">Modifier la quantité de produit</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.isActive ?? true}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              disabled={true}
            />
          }
          label="Produit actif"
          labelPlacement="start"
        />
      </Stack>
      <Grid container spacing={2.5}>
        <Grid size={12}>
          <Box display="flex" gap={2}>
            <TextField
              label="Nom"
              fullWidth
              size="small"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={true}
            />
            <TextField
              label="Référence"
              fullWidth
              size="small"
              required
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              disabled={true}
            />

            <TextField
              label="Catégorie"
              select
              size="small"
              fullWidth
              required
              value={form.categoryId ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  categoryId: Number(e.target.value) || null,
                })
              }
              disabled={true}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Sous-catégorie"
              select
              size="small"
              fullWidth
              value={form.subcategoryId ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  subcategoryId: Number(e.target.value) || null,
                })
              }
              disabled={true}
            >
              {filteredSubcategories.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  {sub.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Garantie"
              type="text"
              fullWidth
              size="small"
              value={form.warrantyPeriod ?? ""}
              onChange={(e) => {
                const value = e.target.value;

                if (/^\d{0,2}$/.test(value)) {
                  setForm({
                    ...form,
                    warrantyPeriod: value === "" ? null : parseInt(value, 10),
                  });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">mois</InputAdornment>
                  ),
                },
              }}
              placeholder="12"
              disabled={true}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Box display="flex" gap={2} alignItems="center">
            <Box sx={{ position: "relative", flex: 1 }}>
              <TextField
                label="Sous-entreprise"
                select
                fullWidth
                size="small"
                value={form.subCompanyId ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    subCompanyId: Number(e.target.value) || null,
                  })
                }
                disabled={true}
              >
                <MenuItem value="">Aucune sous-entreprise</MenuItem>
                {companyStore?.subCompanies.map((sub) => (
                  <MenuItem key={sub?.id} value={sub?.id}>
                    {sub?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <PriceInput
              label="Prix d'achat"
              value={form.purchasePrice ?? null}
              onChange={(value) => setForm({ ...form, purchasePrice: value })}
              disabled={true}
              required
              error={
                form.purchasePrice !== undefined &&
                (form.purchasePrice ?? 0) <= 0
              }
              helperText={
                (form.purchasePrice ?? 0) <= 0
                  ? "Le prix d'achat doit être supérieur à 0"
                  : form.salePrice !== undefined &&
                    form.purchasePrice !== undefined &&
                    Number(form.salePrice) <= Number(form.purchasePrice)
                  ? "Le prix d'achat doit être inférieur au prix de vente"
                  : ""
              }
              size="small"
            />

            <PriceInput
              label="Prix de vente"
              value={form.salePrice ?? null}
              onChange={(value) => setForm({ ...form, salePrice: value })}
              disabled={true}
              required
              error={form.salePrice !== undefined && (form.salePrice ?? 0) <= 0}
              helperText={
                (form.salePrice ?? 0) <= 0
                  ? "Le prix de vente doit être supérieur à 0"
                  : form.salePrice !== undefined &&
                    form.purchasePrice !== undefined &&
                    Number(form.salePrice) <= Number(form.purchasePrice)
                  ? "Le prix de vente doit être supérieur au prix d'achat"
                  : ""
              }
              size="small"
            />

            <PriceInput
              label="Prix promo"
              value={form.promoPrice ?? null}
              onChange={(value) => setForm({ ...form, promoPrice: value })}
              disabled={true}
              required={form.isOnPromo}
              helperText={
                form.isOnPromo
                  ? !form.promoPrice
                    ? "Le prix promo est requis"
                    : form.salePrice != null &&
                      form.promoPrice >= form.salePrice
                    ? "Doit être inférieur au prix de vente"
                    : ""
                  : ""
              }
              size="small"
            />

            <Tooltip
              title={form.isOnPromo ? "Retirer de la promo" : "Mettre en promo"}
            >
              <IconButton
                color={form.isOnPromo ? "success" : "default"}
                onClick={() => setForm({ ...form, isOnPromo: !form.isOnPromo })}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
                disabled={true}
              >
                <LocalOffer />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid size={12}>
          <Box display="flex" gap={2}>
            <NumberInput
              label="Poids"
              value={form.weight ?? ""}
              onChange={(value) => setForm({ ...form, weight: value })}
              disabled={true}
              unit="kg"
              size="small"
            />

            <NumberInput
              label="Longueur"
              value={form.length ?? ""}
              onChange={(value) => setForm({ ...form, length: value })}
              disabled={true}
              unit="cm"
              size="small"
            />

            <NumberInput
              label="Largeur"
              value={form.width ?? ""}
              onChange={(value) => setForm({ ...form, width: value })}
              disabled={true}
              unit="cm"
              size="small"
            />

            <NumberInput
              label="Hauteur"
              value={form.height ?? ""}
              onChange={(value) => setForm({ ...form, height: value })}
              disabled={true}
              unit="cm"
              size="small"
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Typography variant="h6">Variantes disponibles</Typography>

          <Box display="flex" gap={2} mt={1}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.availableColors || []}
              onChange={(_, newValue) =>
                setForm({ ...form, availableColors: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Couleurs disponibles"
                  placeholder="Ajouter une couleur"
                  helperText="Tapez une couleur et appuyez sur Entrée"
                />
              )}
              sx={{ flex: 1 }}
              size="small"
            />

            <Autocomplete
              multiple
              freeSolo
              options={["XS", "S", "M", "L", "XL", "XXL"]}
              value={form.availableSizes || []}
              onChange={(_, newValue) =>
                setForm({ ...form, availableSizes: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tailles disponibles"
                  placeholder="Ajouter une taille"
                  helperText="Choisissez ou ajoutez une taille"
                />
              )}
              sx={{ flex: 1 }}
              size="small"
            />

            <Autocomplete
              multiple
              options={["male", "female", "unisex"]}
              value={form.availableGenders || []}
              onChange={(_, newValue) =>
                setForm({
                  ...form,
                  availableGenders: newValue as (
                    | "male"
                    | "female"
                    | "unisex"
                  )[],
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Genres disponibles"
                  placeholder="Ajouter un genre"
                  helperText="Choisissez un genre"
                />
              )}
              sx={{ flex: 1 }}
              size="small"
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Box display="flex" gap={2}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={form.description ?? ""}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              disabled={true}
              size="small"
            />
          </Box>
        </Grid>

        {/* === Depot Stock Section === */}
        <Grid size={12}>
          <Typography variant="h6">Dépôts disponibles</Typography>
          <FormControl fullWidth>
            <Select
              multiple
              fullWidth
              value={selectedDepotIds}
              onChange={handleDepotSelectChange}
              input={<OutlinedInput label="Dépôts" />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <Typography color="textSecondary">
                      Sélectionnez un dépôt
                    </Typography>
                  );
                }
                return depots
                  .filter((d) => d.isActive)
                  .filter((d) => selected.includes(d.id))
                  .map((d) => d.name)
                  .join(", ");
              }}
              displayEmpty
              disabled={
                loading || depots.filter((d) => d.isActive).length === 0
              }
              size="small"
            >
              {depots.filter((d) => d.isActive).length > 0 ? (
                depots
                  .filter((d) => d.isActive)
                  .map((depot) => (
                    <MenuItem key={depot.id} value={depot.id}>
                      <Checkbox checked={selectedDepotIds.includes(depot.id)} />
                      <ListItemText primary={depot.name} />
                    </MenuItem>
                  ))
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="Aucun dépôt actif disponible" />
                </MenuItem>
              )}
            </Select>
            <FormHelperText>
              {depots.filter((d) => d.isActive).length === 0
                ? "Aucun dépôt actif disponible"
                : depots.filter((d) => d.isActive).length === 1
                ? "Le dépôt est automatiquement sélectionné"
                : "Sélectionnez au moins un dépôt"}
            </FormHelperText>
          </FormControl>
        </Grid>

        {selectedDepotIds?.map((depotId) => {
          const depot = depots.find((d) => d.id === depotId);
          if (!depot) return null;

          const stock = getStockForDepot(depotId);
          // console.log("stock =>", stock);
          return (
            <Grid key={depotId} size={12} sx={styles.depotBox}>
              <Grid
                size={12}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingBottom={1}
              >
                <Typography variant="subtitle1" gutterBottom>
                  {depot.name}
                </Typography>

                <Chip
                  label="Supprimer"
                  color="error"
                  icon={<Delete />}
                  onClick={() => handleDeleteStock(depotId, stock.id)}
                  sx={{ cursor: "pointer", fontWeight: "bold" }}
                />
              </Grid>

              <Grid container spacing={2}>
                <Grid size={4}>
                  <TextField
                    label="Quantité totale"
                    type="number"
                    fullWidth
                    value={stock.totalQuantity ?? 0}
                    disabled
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    label="Quantité ajoutée"
                    type="number"
                    fullWidth
                    value={addedQuantities[depotId] ?? ""}
                    onChange={(e) => handleAddQuantity(depotId, e.target.value)}
                  />
                </Grid>

                <Grid size={4}>
                  <TextField
                    label="Seuil de notification"
                    type="text"
                    fullWidth
                    value={stock.minQuantity ?? "10"}
                    onChange={(e) =>
                      handleStockChange(depotId, "minQuantity", e.target.value)
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Utilisé pour vous notifier lorsque le stock atteint ce seuil">
                              <IconButton size="small">
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={12}>
                  {/* Add Variant Button */}
                  <Button
                    variant="outlined"
                    onClick={() => addVariant(depotId)}
                    sx={{ mb: 2 }}
                    disabled={
                      !(
                        (form.availableColors?.length ?? 0) +
                          (form.availableSizes?.length ?? 0) +
                          (form.availableGenders?.length ?? 0) >
                        0
                      )
                    }
                  >
                    Ajouter Variante
                  </Button>

                  {/* Variants List */}
                  <Stack spacing={2} sx={{ width: "100%" }}>
                    {stock.variants?.map((v, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ display: "flex", width: "100%" }}
                      >
                        {form?.availableColors &&
                          form.availableColors?.length > 0 && (
                            <TextField
                              label="Couleur"
                              select
                              value={v.color ?? ""}
                              onChange={(e) =>
                                handleVariantChange(
                                  depotId,
                                  index,
                                  "color",
                                  e.target.value
                                )
                              }
                              fullWidth
                              sx={{ flex: 1 }}
                            >
                              {form.availableColors.map((color) => (
                                <MenuItem key={color} value={color}>
                                  {color}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                        {form?.availableSizes &&
                          form.availableSizes?.length > 0 && (
                            <TextField
                              label="Taille"
                              select
                              value={v.size ?? ""}
                              onChange={(e) =>
                                handleVariantChange(
                                  depotId,
                                  index,
                                  "size",
                                  e.target.value
                                )
                              }
                              fullWidth
                              sx={{ flex: 1 }}
                            >
                              {form.availableSizes.map((size) => (
                                <MenuItem key={size} value={size}>
                                  {size}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                        {form?.availableGenders &&
                          form.availableGenders?.length > 0 && (
                            <TextField
                              label="Genre"
                              select
                              value={v.gender ?? ""}
                              onChange={(e) =>
                                handleVariantChange(
                                  depotId,
                                  index,
                                  "gender",
                                  e.target.value
                                )
                              }
                              fullWidth
                              sx={{ flex: 1 }}
                            >
                              {form.availableGenders.map((gender) => (
                                <MenuItem key={gender} value={gender}>
                                  {gender}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                        {(form.availableColors?.length ?? 0) +
                          (form.availableSizes?.length ?? 0) +
                          (form.availableGenders?.length ?? 0) >
                          0 && (
                          <>
                            <TextField
                              label="Quantité total"
                              type="number"
                              value={v.totalQuantity ?? 0}
                              onChange={(e) =>
                                handleVariantChange(
                                  depotId,
                                  index,
                                  "totalQuantity",
                                  e.target.value
                                )
                              }
                              sx={{ flex: 1 }}
                              slotProps={{
                                input: {
                                  inputProps: {
                                    min: 0,
                                    max:
                                      getStockAvailableQuantity(stock) +
                                      (v.defectiveQuantity || 0) +
                                      (v.saleQuantity || 0),
                                  },
                                },
                              }}
                              error={
                                v.totalQuantity! > (stock.totalQuantity || 0)
                              }
                            />

                            <IconButton
                              color="error"
                              onClick={() => {
                                setForm((prev) => {
                                  const updatedStocks = [
                                    ...(prev.stocks || []),
                                  ];
                                  const stockIndex = updatedStocks.findIndex(
                                    (s) => s.depotId === depotId
                                  );
                                  if (stockIndex < 0) return prev;
                                  const variants = [
                                    ...(updatedStocks[stockIndex].variants ||
                                      []),
                                  ];
                                  variants.splice(index, 1);
                                  updatedStocks[stockIndex].variants = variants;
                                  return { ...prev, stocks: updatedStocks };
                                });
                              }}
                              sx={{ flexShrink: 0 }}
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    ))}
                    {!isVariantsQuantityValid(stock) && (
                      <Typography color="error" variant="body2">
                        La somme des quantités disponibles des variantes doit
                        être égale à la quantité disponible du dépôt (
                        {getStockAvailableQuantity(stock)}).
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={() => navigate(-1)} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !isFormValid ||
            loading ||
            selectedDepotIds.some((depotId) => {
              const s = getStockForDepot(depotId);
              return !isVariantsQuantityValid(s);
            })
          }
        >
          {loading ? "Enregistrement..." : "Modifier Quantité"}
        </Button>
      </Box>
    </Box>
  );
});

export default ProductFormQuantityPage;
